/**
 * Chip — quick-button preset prompt above Pre-Meeting Blueprint chat (and
 * elsewhere a small action button is needed).
 *
 * States:
 *   - default: navy border, white background, navy text
 *   - primary: filled navy, white text (active selection)
 *   - locked:  greyed-out, lock icon prefix, tier label suffix — for
 *              tier-gated presets the user cannot run on their current tier.
 *              Per the locked Q5 decision (2026-05-02, option 2): visible-but-
 *              locked is the agreed pattern — it's honest with the user and
 *              creates a soft upgrade hook without bait-and-switch behavior.
 *
 * The locked chip is non-clickable (opens an upgrade-context tooltip via the
 * surrounding Pre-Meeting surface; this component only renders the visual).
 */

export default function Chip({
  children,
  onClick,
  primary = false,
  locked = false,
  lockedTierLabel, // e.g. "HR tier" — shown when locked is true
}) {
  if (locked) {
    return (
      <span
        style={{
          fontFamily: "var(--ppr-typography-font-family-ui)",
          fontSize: "var(--ppr-typography-font-size-sm)",
          fontWeight: "var(--ppr-typography-font-weight-semibold)",
          letterSpacing: "-0.005em",
          padding: "8px 15px",
          borderRadius: "var(--ppr-radius-pill)",
          border: "1px solid var(--ppr-color-border-default)",
          background: "var(--ppr-color-background-muted)",
          color: "var(--ppr-color-text-muted)",
          cursor: "not-allowed",
          whiteSpace: "nowrap",
          display: "inline-flex",
          alignItems: "center",
          gap: 8,
        }}
        data-component="Chip"
        data-state="locked"
        aria-disabled="true"
      >
        <LockGlyph />
        <span>{children}</span>
        {lockedTierLabel ? (
          <span
            style={{
              fontFamily: "var(--ppr-typography-font-family-ui)",
              fontSize: "var(--ppr-typography-font-size-xs)",
              fontWeight: "var(--ppr-typography-font-weight-medium)",
              color: "var(--ppr-color-text-muted)",
              textTransform: "uppercase",
              letterSpacing: "var(--ppr-typography-letter-spacing-wider)",
              marginLeft: 2,
            }}
          >
            {lockedTierLabel}
          </span>
        ) : null}
      </span>
    );
  }

  return (
    <button
      onClick={onClick}
      style={{
        fontFamily: "var(--ppr-typography-font-family-ui)",
        fontSize: "var(--ppr-typography-font-size-sm)",
        fontWeight: "var(--ppr-typography-font-weight-medium)",
        letterSpacing: "-0.005em",
        padding: "8px 15px",
        borderRadius: "var(--ppr-radius-pill)",
        border: primary
          ? "1px solid var(--ppr-color-accent-navy)"
          : "1px solid var(--ppr-color-border-default)",
        background: primary
          ? "var(--ppr-color-accent-navy)"
          : "var(--ppr-color-background-surface)",
        color: primary
          ? "var(--ppr-color-background-surface)"
          : "var(--ppr-color-text-primary)",
        cursor: "pointer",
        whiteSpace: "nowrap",
      }}
      data-component="Chip"
      data-state={primary ? "primary" : "default"}
    >
      {children}
    </button>
  );
}

function LockGlyph() {
  return (
    <svg
      width="12"
      height="12"
      viewBox="0 0 16 16"
      fill="none"
      aria-hidden="true"
      data-icon-source="v10-mockup-stub"
    >
      <rect
        x="3"
        y="7"
        width="10"
        height="7"
        rx="1"
        stroke="var(--ppr-color-text-muted)"
        strokeWidth="1.5"
        fill="none"
      />
      <path
        d="M5 7 V 5 A 3 3 0 0 1 11 5 V 7"
        stroke="var(--ppr-color-text-muted)"
        strokeWidth="1.5"
        fill="none"
      />
    </svg>
  );
}
